import React, { createContext, useContext } from 'react';

export enum OnboardingActionType {
  GO_TO_NEXT_STEP = 'GO_TO_NEXT_STEP',
  GO_TO_PREVIOUS_STEP = 'GO_TO_PREVIOUS_STEP',
  INITIAL_DATA = 'INITIAL_DATA',
  SET_ROLE = 'SET_ROLE',
  MARK_PRESETS_SUBMITTED = 'MARK_PRESETS_SUBMITTED',
  MARK_PROFILE_COMPLETED = 'MARK_PROFILE_COMPLETED',
}

export type OnboardingStep = {
  title: string;
  component: JSX.Element;
  skippable?: boolean;
};

export type OnboardingState = {
  currentStep: number;
  steps: OnboardingStep[];
  role?: string;
  finishedOnboarding?: boolean;
  industryHint?: string;
  userEmail?: string;
  orgName?: string;
  demoCompleted?: boolean;
  demoUsedInAppAgent?: boolean;
  // Sample assets were loaded by the Setup step, so DemoMode only has to
  // wait for them to be searchable.
  samplesLoaded?: boolean;
  profileCompleted?: boolean;
  savedPassword?: string;
  savedProfileForm?: { email: string; firstName: string; lastName: string; newPassword: string };
  submittedSchemaPresets: Set<string>;
};

type OnboardingAction = {
  type: OnboardingActionType;
  payload?: any;
};

type OnboardingReducer = (state: OnboardingState, action: OnboardingAction) => OnboardingState;

export const DEFAULT_STATE: OnboardingState = {
  currentStep: 1,
  steps: [],
  submittedSchemaPresets: new Set(),
};

export const reducer: OnboardingReducer = (state, action) => {
  switch (action.type) {
    case OnboardingActionType.GO_TO_NEXT_STEP:
      if (state.currentStep >= state.steps.length) return { ...state, finishedOnboarding: true };
      return {
        ...state,
        currentStep: state.currentStep + 1,
      };
    case OnboardingActionType.GO_TO_PREVIOUS_STEP:
      if (state.currentStep <= 1) return state;
      return {
        ...state,
        currentStep: state.currentStep - 1,
      };
    case OnboardingActionType.INITIAL_DATA:
      return {
        ...state,
        ...action.payload,
      };
    case OnboardingActionType.SET_ROLE:
      return {
        ...state,
        role: action.payload,
      };
    case OnboardingActionType.MARK_PROFILE_COMPLETED: {
      const { password, formSnapshot } = action.payload as {
        password: string;
        formSnapshot: { email: string; firstName: string; lastName: string; newPassword: string };
      };
      return {
        ...state,
        profileCompleted: true,
        savedPassword: password,
        savedProfileForm: formSnapshot,
      };
    }
    case OnboardingActionType.MARK_PRESETS_SUBMITTED: {
      const { schemaPresetIds } = action.payload as {
        schemaPresetIds?: string[];
      };
      const nextSchemas = new Set(state.submittedSchemaPresets);
      if (schemaPresetIds) schemaPresetIds.forEach((id) => nextSchemas.add(id));
      return {
        ...state,
        submittedSchemaPresets: nextSchemas,
      };
    }
    default:
      return state;
  }
};

export const OnboardingContext = createContext<[OnboardingState, React.Dispatch<OnboardingAction>]>([
  DEFAULT_STATE,
  () => {},
]);

export const useOnboardingContext = () => {
  const [state, dispatch] = useContext(OnboardingContext);
  const { currentStep, steps, role, industryHint, userEmail, orgName, demoCompleted, demoUsedInAppAgent, samplesLoaded, profileCompleted, savedPassword, savedProfileForm, submittedSchemaPresets } = state;

  const stepTitle = steps[currentStep - 1]?.title;
  const totalSteps = steps.length;

  const isLastStep = currentStep === totalSteps;
  const isFirstStep = currentStep === 1;
  const skippable = steps[currentStep - 1]?.skippable ?? false;
  const isAdmin = role === 'ADMIN';

  const goToNextStep = () => dispatch({ type: OnboardingActionType.GO_TO_NEXT_STEP });
  const goToPreviousStep = () => dispatch({ type: OnboardingActionType.GO_TO_PREVIOUS_STEP });
  const setDemoCompleted = (val: boolean) =>
    dispatch({ type: OnboardingActionType.INITIAL_DATA, payload: { demoCompleted: val } });
  const setDemoUsedInAppAgent = (val: boolean) =>
    dispatch({ type: OnboardingActionType.INITIAL_DATA, payload: { demoUsedInAppAgent: val } });
  const setSamplesLoaded = (val: boolean) =>
    dispatch({ type: OnboardingActionType.INITIAL_DATA, payload: { samplesLoaded: val } });

  const markPresetsSubmitted = (payload: { schemaPresetIds?: string[] }) =>
    dispatch({ type: OnboardingActionType.MARK_PRESETS_SUBMITTED, payload });

  const markProfileCompleted = (password: string, formSnapshot: { email: string; firstName: string; lastName: string; newPassword: string }) =>
    dispatch({ type: OnboardingActionType.MARK_PROFILE_COMPLETED, payload: { password, formSnapshot } });

  return {
    currentStep,
    stepTitle,
    totalSteps,
    goToNextStep,
    goToPreviousStep,
    isLastStep,
    isFirstStep,
    skippable,
    isAdmin,
    role,
    steps,
    industryHint,
    userEmail,
    orgName,
    demoCompleted,
    setDemoCompleted,
    demoUsedInAppAgent,
    setDemoUsedInAppAgent,
    samplesLoaded,
    setSamplesLoaded,
    profileCompleted,
    savedPassword,
    savedProfileForm,
    markProfileCompleted,
    submittedSchemaPresets,
    markPresetsSubmitted,
  };
};
